import { useEffect, useState } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { BRYQ_TESTS, defaultBryqKey, fetchBryqTests, type BryqKey, type BryqTest } from '../../services/bryqApi';

/**
 * "Send Bryq in parallel to this interview" — the optional line in every invite dialog (Oct 2026).
 * The other mode, Bryq as a FILTER step before any interview, is the 🧠 chip on the card.
 *
 * Off by default: most invites go without one. Ticked, it preselects Cognitive in the dialog's
 * language (the same DE/EN the email is in), so the common case is one click. The Bryq step runs
 * on the server only AFTER the invite email went out — a stopped countdown sends neither.
 *
 * `value` is the chosen test, or null for "no Bryq". `opportunityId` (any candidate in the dialog)
 * lets the field grey out tests that have no posting in Lever yet.
 */
export function BryqAlongsideField({
  value,
  onChange,
  language,
  opportunityId,
  disabled,
}: {
  value: BryqKey | null;
  onChange: (next: BryqKey | null) => void;
  language: 'de' | 'en';
  opportunityId?: string;
  disabled?: boolean;
}) {
  const [tests, setTests] = useState<BryqTest[]>(BRYQ_TESTS);
  const ticked = value !== null;

  useEffect(() => {
    if (!ticked || !opportunityId) return;
    let live = true;
    fetchBryqTests(opportunityId)
      .then((t) => live && setTests(t))
      .catch(() => {}); // the static list stays; the server still refuses a test it cannot send
    return () => {
      live = false;
    };
  }, [ticked, opportunityId]);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label className="inline-flex items-center gap-2">
        <Checkbox
          checked={ticked}
          disabled={disabled}
          onCheckedChange={(on: boolean) => onChange(on ? defaultBryqKey(language) : null)}
          aria-label="Send Bryq in parallel to this interview"
        />
        Send Bryq in parallel to this interview
      </label>
      {ticked && (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value as BryqKey)}
          disabled={disabled}
          className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
          aria-label="Which Bryq test"
        >
          {tests.map((t) => (
            <option key={t.key} value={t.key} disabled={!t.available}>
              {t.label}
              {t.available ? '' : ' (not set up in Lever yet)'}
            </option>
          ))}
        </select>
      )}
      {ticked && (
        <span className="w-full text-xs text-gray-500">
          The email below mentions it; Bryq sends the test link in its own email.
        </span>
      )}
    </div>
  );
}
